import { HTMLAttributes, useState } from 'react';
import Dialog from '@/dashboard/components/Dialog/index';
import FooterView from '@/dashboard/components/Products/policyCard/components/footer/index';
import HeaderView from '@/dashboard/components/Products/policyCard/components/header/index';
import PendingSummaryView from '@/dashboard/components/Products/policyCard/components/pendingSummary/index';
import PolicyProductInfoView from '@/dashboard/components/Products/policyCard/components/policyProductInfo/index';
import ProductsView from '@/dashboard/components/Products/policyCard/components/products/index';
import SwitchToAnnualView from '@/dashboard/components/Products/policyCard/dialogs/switchToAnnual/index';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { usePolicyDetailStore } from '@/dashboard/store/PolicyDetailStore';
import { getSwitchToAnnualPolicyPath } from '@/dashboard/utils/routes';
import { JoveLogoLoading } from '@jove-frontend/mui';
import { PolicyState, PolicyStatus, PolicyStatusView, formatCommonDateStr } from '@jove-frontend/uikit';
import { WarningOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';

type PolicyCardProps = HTMLAttributes<HTMLDivElement> & {
  policy: PolicyModel;
};

const PolicyCard = (props: PolicyCardProps) => {
  const { policy, className } = props;
  const [showSwitchToAnnualDialog, setShowSwitchToAnnualDialog] = useState(false);
  const [{ cleanAllPolicyDetailInfo }] = usePolicyDetailStore((store) => [store.actions]);
  const [loading, setLoading] = useState(false);
  const { push } = useProjectNavigate();

  if (!policy) return;

  /**
   * methods
   */
  // switch to annual
  const switchToAnnualAction = () => {
    setShowSwitchToAnnualDialog(true);
  };

  // dialog click switch to annual
  const chooseAnnualAction = () => {
    cleanAllPolicyDetailInfo();
    push({ path: getSwitchToAnnualPolicyPath(policy?.id) });
  };

  const emitLoadingAction = (value) => {
    setLoading(value);
  };

  return (
    <div className={clsx('relative rounded-xl bg-white p-4', className)}>
      {loading && <JoveLogoLoading />}
      {policy.state === PolicyState.POLICY_ELAPSED && <Box className="bg-grey-70/15 absolute left-0 top-0 z-10 h-full w-full rounded-xl"></Box>}
      <HeaderView className="mb-4" policy={policy} switchToAnnual={switchToAnnualAction} emitLoading={emitLoadingAction}></HeaderView>
      <div className="mb-2 flex flex-col">
        <div className="flex flex-row items-start justify-between">
          <Typography className="text-black-secondary font-black" variant="subtitle1">
            Policy number:
            <Typography className="text-black-secondary ml-2 inline-block font-medium" variant="subtitle1" color="initial">
              {policy?.policyNumber}
            </Typography>
          </Typography>
          {policy.status && <PolicyStatusView status={policy.status} />}
        </div>
        {[PolicyStatus.POLICY_UPDATE_QUOTE_CONFIRMED, PolicyStatus.POLICY_UPDATE_QUOTE].includes(policy.status) && (
          <div className="flex flex-row justify-end">
            <Typography variant="subtitle2" className="text-brand-primary font-bold">
              {[PolicyStatus.POLICY_UPDATE_QUOTE].includes(policy.status) ? 'Expected ' : ''} Effective date:{' '}
              {formatCommonDateStr(policy?.payload?.update_effective_date)}
            </Typography>
          </div>
        )}
        {!!policy.renewalQuotePayload && (
          <div className="flex flex-row items-center">
            <Typography className="text-black-secondary font-black" variant="subtitle1">
              Policy period:
              <span className="ml-2 text-sm font-medium text-[#FF9F1C]">
                {policy.policyStartDateText} - {policy.policyEndDateText}
                {policy.dayLeft > 0 && <>（{policy.dayLeft} days left）</>}
              </span>
            </Typography>
          </div>
        )}
        {policy.renewalCancelled && (
          <div className="flex flex-row space-x-2">
            <WarningOutlined className="h-5 w-5 text-[#E6C30C]" />
            <p className="text-sm font-bold">
              Since the renewal of the current policy has been canceled, it will not be automatically renewed upon expiration.
            </p>
          </div>
        )}
      </div>

      {policy.isChubb && policy.canShowCovers && <ProductsView policy={policy} />}
      {!policy.isChubb && <PolicyProductInfoView policy={policy} />}
      {policy.canShowPausingSummary && <PendingSummaryView className="mt-3" policy={policy} />}
      <FooterView className="mt-4" policy={policy} switchToAnnual={switchToAnnualAction}></FooterView>
      {showSwitchToAnnualDialog && (
        <Dialog close={() => setShowSwitchToAnnualDialog(false)} width="800px">
          <SwitchToAnnualView className="mt-5" policy={policy} close={() => setShowSwitchToAnnualDialog(false)} chooseAnnual={chooseAnnualAction} />
        </Dialog>
      )}
    </div>
  );
};

export default PolicyCard;
